import type { DateTime } from "luxon";
import { OutreachApproval, type OutreachSignUp, OutreachStatus, type RequestOutreach } from "@/utils/models";
import { isRecommendedSignUp } from "@/utils/outreachRules";

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

type WaitUnit = "day" | "hour" | "minute";

/** One unit in the locale's own narrow form: "3h" / "3 שע׳", "20m" / "20min". */
const unitOf = (locale: string, unit: WaitUnit, value: number): string =>
	new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "narrow" }).format(value);

/**
 * "45m", "3h 20m", "2d 4h" (in the locale's units): how long someone waited, rounded to the minute and never under
 * one, to its two largest units.
 */
export const waitOf = (from: DateTime, to: DateTime, locale: string): string => {
	const total = Math.max(1, Math.round(to.diff(from, "minutes").minutes));
	const days = Math.floor(total / MINUTES_PER_DAY);
	const hours = Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
	const minutes = total % MINUTES_PER_HOUR;
	const parts: [WaitUnit, number][] =
		days > 0
			? [
					["day", days],
					["hour", hours],
				]
			: [
					["hour", hours],
					["minute", minutes],
				];
	const shown = parts.filter(([, value]) => value > 0);
	return new Intl.ListFormat(locale, { type: "unit", style: "narrow" }).format(shown.map(([unit, value]) => unitOf(locale, unit, value)));
};

export enum ApprovalViewKind {
	/** Someone accepted and waits on you. */
	Waiting = "waiting",
	/** The latest decision, once nobody waits. */
	Decided = "decided",
}

export type ApprovalView =
	| {
			kind: ApprovalViewKind.Waiting;
			signUp: OutreachSignUp;
			recommended: boolean;
			/** How long they've waited so far. */
			wait: string;
			/** Others waiting behind them. */
			moreWaiting: number;
			/** Whether the department auto-approves: on, this one didn't fit its rules; off, what turning it on would save. */
			autoApprove: boolean;
			/** No open spot left: approving them would put more people on the shift than it needs. */
			full: boolean;
	  }
	| {
			kind: ApprovalViewKind.Decided;
			signUp: OutreachSignUp;
			filled: boolean;
			openSpots: number;
			/** For a manager's approval while auto-approve is off: how much sooner auto-approve would have had them on. */
			savedWait: string | null;
	  };

/** Spots still open; never below zero, though real responses can hold more approvals than needed. */
const openSpotsOf = (outreach: Pick<RequestOutreach, "shift" | "counts">): number => Math.max(0, outreach.shift.neededCount - outreach.counts.approved);

const requireDecidedAt = (signUp: OutreachSignUp): DateTime => {
	if (signUp.decidedAt === null) throw new Error(`Outreach: decided sign-up ${signUp.nurseId} has no decision time`);
	return signUp.decidedAt;
};

/**
 * The approval callout: whoever has waited longest first (that needs you), else the latest decision; nothing while
 * nobody has accepted.
 */
export const approvalViewOf = (
	outreach: Pick<RequestOutreach, "signUps" | "autoApprove" | "status" | "shift" | "counts">,
	now: DateTime,
	/** The UI language, for the wait's units. */
	locale: string,
): ApprovalView | null => {
	const { signUps, autoApprove } = outreach;
	// Newest first, so the last waiting one has waited longest.
	const waiting = signUps.filter((signUp) => signUp.approval === OutreachApproval.Waiting);
	const first = waiting.at(-1);
	if (first !== undefined) {
		return {
			kind: ApprovalViewKind.Waiting,
			signUp: first,
			recommended: isRecommendedSignUp(first.check),
			wait: waitOf(first.signedUpAt, now, locale),
			moreWaiting: waiting.length - 1,
			autoApprove,
			full: openSpotsOf(outreach) === 0,
		};
	}
	const decided = signUps.filter((signUp) => signUp.decidedAt !== null).sort((a, b) => requireDecidedAt(b).toMillis() - requireDecidedAt(a).toMillis());
	const latest = decided[0];
	if (latest === undefined) return null;
	const byManager = latest.approval === OutreachApproval.ByManager && !autoApprove;
	return {
		kind: ApprovalViewKind.Decided,
		signUp: latest,
		filled: outreach.status === OutreachStatus.Filled,
		openSpots: openSpotsOf(outreach),
		savedWait: byManager ? waitOf(latest.signedUpAt, requireDecidedAt(latest), locale) : null,
	};
};
